import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { Trash2, RotateCcw, ArrowLeft, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const AdminDeleted = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [deletedAppointments, setDeletedAppointments] = useState([]);
  const [deletedIntakes, setDeletedIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApptIds, setSelectedApptIds] = useState(new Set());
  const [selectedIntakeIds, setSelectedIntakeIds] = useState(new Set());

  const estDateTime = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-US", {
        timeZone: "America/New_York",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }) + " EST";
    } catch {
      return String(iso);
    }
  };

  // Check for existing auth on mount
  useEffect(() => {
    const storedAuth = sessionStorage.getItem('adminAuth');
    if (storedAuth) {
      try {
        const decoded = atob(storedAuth);
        const [username, password] = decoded.split(':');
        setCredentials({ username, password });
        setIsAuthenticated(true);
      } catch {
        sessionStorage.removeItem('adminAuth');
        navigate('/admin');
      }
    } else {
      navigate('/admin');
    }
  }, [navigate]);

  const getAuthConfig = useCallback(() => {
    if (!credentials) return {};
    return {
      auth: {
        username: credentials.username,
        password: credentials.password
      }
    };
  }, [credentials]);

  const fetchData = useCallback(async () => {
    if (!credentials) return;
    
    setLoading(true);
    try {
      const config = getAuthConfig();
      const [deletedApptsRes, deletedIntakesRes] = await Promise.all([
        axios.get(`${API}/admin/deleted/appointments`, config),
        axios.get(`${API}/admin/deleted/intakes`, config),
      ]);
      setDeletedAppointments(deletedApptsRes.data || []);
      setDeletedIntakes(deletedIntakesRes.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem('adminAuth');
        navigate('/admin');
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to fetch deleted items");
      }
    } finally {
      setLoading(false);
    }
  }, [credentials, getAuthConfig, navigate]);

  useEffect(() => {
    if (isAuthenticated && credentials) {
      fetchData();
    }
  }, [isAuthenticated, credentials, fetchData]);

  // Appointment actions
  const restoreAppointment = async (appointmentId) => {
    try {
      await axios.post(`${API}/admin/deleted/appointments/${appointmentId}/restore`, {}, getAuthConfig());
      toast.success("Appointment restored");
      fetchData();
    } catch {
      toast.error("Failed to restore appointment");
    }
  };

  const permanentlyDeleteAppointment = async (appointmentId) => {
    const ok = window.confirm("Permanently delete this appointment? This cannot be undone.");
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/deleted/appointments/${appointmentId}`, getAuthConfig());
      toast.success("Appointment permanently deleted");
      setSelectedApptIds(prev => {
        const next = new Set(prev);
        next.delete(appointmentId);
        return next;
      });
      fetchData();
    } catch {
      toast.error("Failed to permanently delete appointment");
    }
  };

  const bulkRestoreAppointments = async () => {
    const ids = Array.from(selectedApptIds);
    if (ids.length === 0) return;
    try {
      await Promise.all(ids.map(id => 
        axios.post(`${API}/admin/deleted/appointments/${id}/restore`, {}, getAuthConfig())
      ));
      toast.success(`${ids.length} appointment(s) restored`);
      setSelectedApptIds(new Set());
      fetchData();
    } catch {
      toast.error("Failed to restore appointments");
    }
  };

  const bulkDeleteAppointments = async () => {
    const ids = Array.from(selectedApptIds);
    if (ids.length === 0) return;
    const ok = window.confirm(`Permanently delete ${ids.length} appointment(s)? This cannot be undone.`);
    if (!ok) return;
    try {
      await Promise.all(ids.map(id => 
        axios.delete(`${API}/admin/deleted/appointments/${id}`, getAuthConfig())
      ));
      toast.success(`${ids.length} appointment(s) permanently deleted`);
      setSelectedApptIds(new Set());
      fetchData();
    } catch {
      toast.error("Failed to delete appointments");
    }
  };

  // Intake actions
  const restoreIntake = async (intakeId) => {
    try {
      await axios.post(`${API}/admin/deleted/intakes/${intakeId}/restore`, {}, getAuthConfig());
      toast.success("Client restored");
      fetchData();
    } catch {
      toast.error("Failed to restore client");
    }
  };

  const permanentlyDeleteIntake = async (intakeId) => {
    const ok = window.confirm("Permanently delete this client? This cannot be undone.");
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/deleted/intakes/${intakeId}`, getAuthConfig());
      toast.success("Client permanently deleted");
      setSelectedIntakeIds(prev => {
        const next = new Set(prev);
        next.delete(intakeId);
        return next;
      });
      fetchData();
    } catch {
      toast.error("Failed to permanently delete client");
    }
  };

  const bulkRestoreIntakes = async () => {
    const ids = Array.from(selectedIntakeIds);
    if (ids.length === 0) return;
    try {
      await Promise.all(ids.map(id => 
        axios.post(`${API}/admin/deleted/intakes/${id}/restore`, {}, getAuthConfig())
      ));
      toast.success(`${ids.length} client(s) restored`);
      setSelectedIntakeIds(new Set());
      fetchData();
    } catch {
      toast.error("Failed to restore clients");
    }
  };

  const bulkDeleteIntakes = async () => {
    const ids = Array.from(selectedIntakeIds);
    if (ids.length === 0) return;
    const ok = window.confirm(`Permanently delete ${ids.length} client(s)? This cannot be undone.`);
    if (!ok) return;
    try {
      await Promise.all(ids.map(id => 
        axios.delete(`${API}/admin/deleted/intakes/${id}`, getAuthConfig())
      ));
      toast.success(`${ids.length} client(s) permanently deleted`);
      setSelectedIntakeIds(new Set());
      fetchData();
    } catch {
      toast.error("Failed to delete clients");
    }
  };

  // Selection handlers
  const toggleApptSelection = (id, checked) => {
    setSelectedApptIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAllAppts = (checked) => {
    if (checked) {
      setSelectedApptIds(new Set(deletedAppointments.map(a => a.id)));
    } else {
      setSelectedApptIds(new Set());
    }
  };

  const toggleIntakeSelection = (id, checked) => {
    setSelectedIntakeIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAllIntakes = (checked) => {
    if (checked) {
      setSelectedIntakeIds(new Set(deletedIntakes.map(i => i.id)));
    } else {
      setSelectedIntakeIds(new Set());
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-deleted-page">
      <Header showAdminLink={true} />
      
      <main className="pt-[105px] md:pt-21 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                className="border-navy text-navy hover:bg-navy hover:text-white"
                data-testid="btn-back"
              >
                <ArrowLeft className="mr-2" size={18} />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="font-heading font-bold text-3xl text-navy flex items-center gap-3">
                  <Trash2 className="text-red-500" size={32} />
                  Deleted Items
                </h1>
                <p className="text-slate-600 mt-1">Review and manage deleted appointments and clients</p>
              </div>
            </div>
            <Button
              onClick={fetchData}
              variant="outline"
              className="border-navy text-navy hover:bg-navy hover:text-white mt-4 md:mt-0"
              data-testid="btn-refresh"
            >
              <RefreshCw className="mr-2" size={18} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto" />
              <p className="text-slate-500 mt-3">Loading deleted items...</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Deleted Appointments */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="border-0 shadow-card" data-testid="deleted-appointments-card">
                  <CardHeader className="border-b bg-slate-50/50">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <CardTitle className="font-heading text-navy text-xl font-bold flex items-center gap-2">
                        Deleted Appointments
                        <span className="text-sm font-normal bg-red-100 text-red-600 px-2 py-1 rounded-full">
                          {deletedAppointments.length}
                        </span>
                      </CardTitle>
                      {deletedAppointments.length > 0 && (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={bulkRestoreAppointments}
                            disabled={selectedApptIds.size === 0}
                            className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white h-9"
                            data-testid="btn-bulk-restore-appts"
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Restore ({selectedApptIds.size})
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={bulkDeleteAppointments}
                            disabled={selectedApptIds.size === 0}
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white h-9"
                            data-testid="btn-bulk-delete-appts"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete Forever ({selectedApptIds.size})
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {deletedAppointments.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">
                        No deleted appointments
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table className="data-table">
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-12">
                                <Checkbox
                                  checked={selectedApptIds.size === deletedAppointments.length && deletedAppointments.length > 0}
                                  onCheckedChange={toggleAllAppts}
                                  aria-label="Select all appointments"
                                />
                              </TableHead>
                              <TableHead>Client Name</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Appointment Time</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {deletedAppointments.map((appt) => (
                              <TableRow key={appt.id}>
                                <TableCell>
                                  <Checkbox
                                    checked={selectedApptIds.has(appt.id)}
                                    onCheckedChange={(checked) => toggleApptSelection(appt.id, checked)}
                                  />
                                </TableCell>
                                <TableCell className="font-medium">{appt.client_name}</TableCell>
                                <TableCell>{appt.client_email}</TableCell>
                                <TableCell>{estDateTime(appt.start_time)}</TableCell>
                                <TableCell>
                                  <div className="flex gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => restoreAppointment(appt.id)}
                                      className="h-8 border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                                    >
                                      <RotateCcw className="w-3 h-3 mr-1" />
                                      Restore
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => permanentlyDeleteAppointment(appt.id)}
                                      className="h-8 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                                    >
                                      <Trash2 className="w-3 h-3 mr-1" />
                                      Delete Forever
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

              {/* Deleted Clients */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Card className="border-0 shadow-card" data-testid="deleted-clients-card">
                  <CardHeader className="border-b bg-slate-50/50">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <CardTitle className="font-heading text-navy text-xl font-bold flex items-center gap-2">
                        Deleted Clients
                        <span className="text-sm font-normal bg-red-100 text-red-600 px-2 py-1 rounded-full">
                          {deletedIntakes.length}
                        </span>
                      </CardTitle>
                      {deletedIntakes.length > 0 && (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={bulkRestoreIntakes}
                            disabled={selectedIntakeIds.size === 0}
                            className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white h-9"
                            data-testid="btn-bulk-restore-intakes"
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Restore ({selectedIntakeIds.size})
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={bulkDeleteIntakes}
                            disabled={selectedIntakeIds.size === 0}
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white h-9"
                            data-testid="btn-bulk-delete-intakes"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete Forever ({selectedIntakeIds.size})
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {deletedIntakes.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">
                        No deleted clients
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table className="data-table">
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-12">
                                <Checkbox
                                  checked={selectedIntakeIds.size === deletedIntakes.length && deletedIntakes.length > 0}
                                  onCheckedChange={toggleAllIntakes}
                                  aria-label="Select all clients"
                                />
                              </TableHead>
                              <TableHead>Client Name</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Phone</TableHead>
                              <TableHead>Submitted</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {deletedIntakes.map((intake) => (
                              <TableRow key={intake.id}>
                                <TableCell>
                                  <Checkbox
                                    checked={selectedIntakeIds.has(intake.id)}
                                    onCheckedChange={(checked) => toggleIntakeSelection(intake.id, checked)}
                                  />
                                </TableCell>
                                <TableCell className="font-medium">
                                  {intake.contact?.first_name} {intake.contact?.last_name}
                                </TableCell>
                                <TableCell>{intake.contact?.email}</TableCell>
                                <TableCell>{intake.contact?.phone || '-'}</TableCell>
                                <TableCell>{estDateTime(intake.created_at)}</TableCell>
                                <TableCell>
                                  <div className="flex gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => restoreIntake(intake.id)}
                                      className="h-8 border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                                    >
                                      <RotateCcw className="w-3 h-3 mr-1" />
                                      Restore
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => permanentlyDeleteIntake(intake.id)}
                                      className="h-8 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                                    >
                                      <Trash2 className="w-3 h-3 mr-1" />
                                      Delete Forever
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminDeleted;
